import { useMemo } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ActionItemStatus } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { useProviderActivationEvents } from '@uplift/queries';
import { Modal, ModalContent, ModalTitle } from '@uplift/ui-v2';

import {
  ApprovedStepSection, InReviewStepSection, OnboardingTask, ProgressSummaryBar, ToDoStepSection
} from '../components';
import { MSO_PROVIDER_PROGRESS_THRESHOLDS, REGULAR_PROGRESS_THRESHOLDS } from '../constants';
import { eventsToStatus, getActiveStep, ProviderOnboardingToDoStatus } from '../helpers';
import { OnboardingItems, OnboardingSteps } from '../types';

export const DashboardModal: React.VFC = () => {
  const user = useCurrentuser();
  const { data: events, isLoading: eventsIsLoading } = useProviderActivationEvents(user?.id);
  const isMobile = useIsMobile();
  const { classes, cx } = useStyles();
  const l10n = useL10n();
  const isProviderMSOPartner = user?.rules?.msoPartner;
  const activeStep = getActiveStep(events);
  const theme = useTheme();

  const { todoItems, progressSummaryItems, progressPercentage } = useMemo<OnboardingItems>(() => {
    const progressSummaryItems = [];
    const todoItems = [];
    const statuses = eventsToStatus(events);
    const statusKeys = Object.keys(statuses) as ProviderOnboardingToDoStatus[];

    if (statuses.credentialing === ActionItemStatus.approved) {
      // move stateSelection to the top of the list
      statusKeys.splice(statusKeys.indexOf('stateSelection'), 1);
      statusKeys.unshift('stateSelection');
    }

    if (statuses.mfaAdded !== ActionItemStatus.done) {
      // move mfa to top
      statusKeys.splice(statusKeys.indexOf('mfaAdded'), 1);
      statusKeys.unshift('mfaAdded');
    }

    statusKeys.forEach((task) => {
      const status = statuses[task];

      if (status === ActionItemStatus.todo) {
        todoItems.push({ task, status });
      } else {
        progressSummaryItems.push({ task, status });
      }
    });

    const regularProgressPercentage = REGULAR_PROGRESS_THRESHOLDS[progressSummaryItems.length];
    const msoProviderProgressPercentage = MSO_PROVIDER_PROGRESS_THRESHOLDS[progressSummaryItems.length];
    const progressPercentage = isProviderMSOPartner ? msoProviderProgressPercentage : regularProgressPercentage;

    return { progressSummaryItems, todoItems: todoItems.filter(Boolean), progressPercentage };
  }, [ isProviderMSOPartner, events ]);

  const toDoSection = useMemo(() => {
    switch (activeStep) {
      case OnboardingSteps.ToDo:
        return <ToDoStepSection toDos={todoItems}/>;
      case OnboardingSteps.InReview:
        return <InReviewStepSection />;
      case OnboardingSteps.Approved:
        return <ApprovedStepSection />;
      default:
        return null;
    }
  }, [ todoItems, activeStep ]);

  if (eventsIsLoading) {
    return null;
  }

  const questionEmail = (
    <Box display="flex" justifyContent="center" p={2}>
      <Typography variant="body2">
        {l10n.providerSettings.onboarding.haveAQuestion}&nbsp;
        <Typography
          variant="body2"
          component="a"
          href="https://provider-help.joinuplift.co"
          target="_blank"
          rel="noopener noreferrer"
        >
          {l10n.providerSettings.onboarding.helpCenter}
        </Typography>
      </Typography>
    </Box>
  );

  return (
    (
      <Modal
        open
        showCloseIcon={false}
        classes={{
          root: !isMobile && classes.modalRoot,
          paperWidthXl: !isMobile && classes.paperWidthXl
        }}
        maxWidth={isMobile ? null : 'xl'}
        fullScreen={isMobile}
        style={{ zIndex: theme.zIndex.appBar - 1 }}
      >
        <ModalTitle classes={{ title: classes.titleBgColor, root: classes.root }}>
          <Box display="flex" p={1}>
            <Box className={classes.modalTitle}>
              {l10n.providerSettings.onboarding.gettingStarted}
            </Box>
          </Box>
        </ModalTitle>
        <Divider />
        {isMobile && (
          <Box className={classes.mobileModalSubtitle}>
            <Typography variant="body1" style={{ fontWeight: '600' }}>{l10n.providerSettings.onboarding.gettingStarted}</Typography>
          </Box>
        )}
        <ModalContent noPadding>
          <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} flexGrow={1}>
            <Box className={cx(classes.progressSummaryBlock, !isMobile && classes.progressSummaryBlockDesktop)}>
              <Accordion defaultExpanded={!isMobile} elevation={0}>
                <AccordionSummary
                  className={classes.progressSummary}
                  expandIcon={<ExpandMoreIcon fontSize="small"/>}
                >
                  <Box pl={3} pt={4}><Typography className={classes.progressSummaryTitle}>{l10n.providerSettings.onboarding.progressSummary}</Typography></Box>
                  <Box className={classes.progressSummaryStep}>
                    <ProgressSummaryBar percent={progressPercentage} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.progressSummaryDetails}>
                  {progressSummaryItems.map((toDo, index) => (
                    <OnboardingTask key={index} {...toDo} />
                  ))}
                </AccordionDetails>
              </Accordion>
              {!isMobile && questionEmail}
            </Box>
            <Box display="flex" flexDirection="column" flexGrow={1}>
              <Box display="flex" flexDirection="column" flexGrow={1} p={isMobile ? 0 : 6}>
                {toDoSection}
              </Box>
            </Box>
          </Box>
          {isMobile && (
            <>
              <Divider className={classes.divider} />
              {questionEmail}
            </>
          )}
        </ModalContent>
      </Modal>
    )
  );
};

const useStyles = makeStyles()(theme => ({
  root: {
    padding: 0,
  },
  modalRoot: {
    inset: '1rem !important',
  },
  titleBgColor: {
    backgroundColor: theme.palette.grey[100],
  },
  modalTitle: {
    display: 'flex',
    flexGrow: 1,
    color: theme.palette.darkSlate.main,
    fontSize: '1rem',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
    fontWeight: 600,
    letterSpacing: '0.5px',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.125rem',
      letterSpacing: '0.15px',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.125rem',
      justifyContent: 'center',
    },
  },
  mobileModalSubtitle: {
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    paddingTop: '0.8rem',
    paddingLeft: '1rem',
    backgroundColor: theme.palette.grey[100],
    boxShadow: `inset 0 20px 10px -5px ${theme.palette.grey[300]}`,
  },
  progressSummaryTitle: {
    color: theme.palette.darkSlate.main,
    fontSize: '1.25rem',
    [theme.breakpoints.up('md')]: {
      fontSize: '1.5rem'
    },
    fontWeight: 700,
  },
  paperWidthXl: {
    marginTop: '6rem',
    marginBottom: '2rem',
    width: '1040px',
    height: '680px'
  },
  progressSummaryBlock: {
    display: 'flex',
    flexDirection: 'column',
  },
  progressSummaryBlockDesktop: {
    justifyContent: 'space-between',
    minWidth: '350px',
    borderRight: `1px solid ${theme.palette.grey[300]}`
  },
  progressSummary: {
    padding: 0,
    position: 'relative',
    display: 'flex',
    '& .Mui-expanded': {
      margin: 0,
    },
    '& > div': {
      flexDirection: 'column',
      display: 'flex',
      margin: 0,
      '&:last-child': {
        position: 'absolute',
        right: '1rem',
        marginBottom: '1rem',
        display: 'block',
        [theme.breakpoints.up('md')]: {
          display: 'none',
        },
      }
    }
  },
  progressSummaryDetails: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  progressSummaryStep: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    padding: theme.spacing(3)
  },
  labelWeight: {
    fontWeight: 450,
  },
  divider: {
    display: 'flex',
    width: '100%',
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    '& svg path': {
      fill: theme.palette.darkSlate.main
    }
  },
}));
